import { type ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Text } from '@mantine/core';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import styles from './AutoSaveIndicator.module.css';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus;
}

const ICON_SIZE = 14;

export const AutoSaveIndicator = memo(function AutoSaveIndicator({
  status,
}: AutoSaveIndicatorProps): ReactElement | null {
  const { t } = useTranslation();

  if (status === 'idle') {
    return null;
  }

  return (
    <div className={styles.container} data-status={status}>
      {status === 'saving' && (
        <>
          <Loader size="xs" className={styles.icon} />
          <Text size="xs" className={styles.text}>
            {t('settings.autoSave.saving')}
          </Text>
        </>
      )}
      {status === 'saved' && (
        <>
          <CheckCircle size={ICON_SIZE} className={styles.icon} />
          <Text size="xs" className={styles.text}>
            {t('settings.autoSave.saved')}
          </Text>
        </>
      )}
      {status === 'error' && (
        <>
          <AlertTriangle size={ICON_SIZE} className={styles.icon} />
          <Text size="xs" className={styles.text}>
            {t('settings.autoSave.error')}
          </Text>
        </>
      )}
    </div>
  );
});
